import type { Country } from '@backmarket/http-api'

import type {
  Base64File,
  BusinessDetails,
  Stakeholder,
  StakeholderRole,
} from '~/api/kyb/types'
import type { BusinessFormValues } from '~/scopes/kyb/pages/components/steps/BusinessDetails/BusinessDetails.types'
import { IDENTIFICATION_TYPES } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.constants'
import type { StakeholderFormValues } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.types'

export function convertFileToBase64(file: File): Base64File {
  // TODO: Implement the base64 conversion
  return {
    base64content: 'mock-base64-content',
    mimetype: file.type,
    filename: file.name,
  }
}

export function transformStakeholderFormToApi(
  formValues: StakeholderFormValues,
  roles: StakeholderRole[],
  id?: string | null,
): Stakeholder {
  if (formValues.photoIdFront.length === 0) {
    throw new Error('Photo ID front is required')
  }

  const photoIdFront = convertFileToBase64(formValues.photoIdFront[0])
  const photoIdBack =
    formValues.photoIdBack.length > 0
      ? convertFileToBase64(formValues.photoIdBack[0])
      : null

  return {
    id: id ?? null,
    roles,
    firstName: formValues.firstName,
    kanaFirstName: formValues.kanaFirstName,
    latinFirstName: formValues.latinFirstName,
    lastName: formValues.lastName,
    kanaLastName: formValues.kanaLastName,
    latinLastName: formValues.latinLastName,
    dateOfBirth: formValues.dateOfBirth,
    countryOfBirth: formValues.countryOfBirth,
    countryOfCitizenship: formValues.countryOfCitizenship,
    phoneNumber: formValues.phoneNumber,
    countryOfResidency: formValues.countryOfResidency,
    address: {
      addressLine1: formValues.addressLine1,
      addressLine2: formValues.addressLine2,
      postalCode: formValues.postalCode,
      city: formValues.city,
      country: formValues.country as string,
    },
    identityDocument: {
      identificationType: formValues.identificationType,
      countryOfIssue: formValues.countryOfIssue,
      front: photoIdFront,
      back: photoIdBack,
    },
  }
}

export function createStakeholderFormValues(
  stakeholder: Stakeholder | undefined,
): StakeholderFormValues {
  return {
    id: stakeholder?.id ?? null,
    firstName: stakeholder?.firstName ?? '',
    kanaFirstName: stakeholder?.kanaFirstName ?? '',
    latinFirstName: stakeholder?.latinFirstName ?? '',
    lastName: stakeholder?.lastName ?? '',
    kanaLastName: stakeholder?.kanaLastName ?? '',
    latinLastName: stakeholder?.latinLastName ?? '',
    dateOfBirth: stakeholder?.dateOfBirth ?? '',
    countryOfBirth: stakeholder?.countryOfBirth ?? '',
    countryOfCitizenship: stakeholder?.countryOfCitizenship ?? '',
    phoneNumber: stakeholder?.phoneNumber ?? '',
    countryOfResidency: stakeholder?.countryOfResidency ?? '',
    addressLine1: stakeholder?.address?.addressLine1 ?? '',
    addressLine2: stakeholder?.address?.addressLine2 ?? '',
    postalCode: stakeholder?.address?.postalCode ?? '',
    city: stakeholder?.address?.city ?? '',
    country: (stakeholder?.address?.country as Country | '') ?? '',
    identificationType:
      stakeholder?.identityDocument?.identificationType ??
      IDENTIFICATION_TYPES.PASSPORT,
    countryOfIssue: stakeholder?.identityDocument?.countryOfIssue ?? '',
    photoIdFront: [],
    photoIdBack: [],
  }
}

export function createEmptyStakeholderFormValues(): StakeholderFormValues {
  return createStakeholderFormValues(undefined)
}

export function transformBusinessDetailsFormToApi(
  formValues: BusinessFormValues,
): BusinessDetails {
  return {
    legalName: formValues.legalName,
    tradeName: formValues.tradeName,
    kanaLegalCompanyName: formValues.kanaLegalName || undefined,
    latinLegalCompanyName: formValues.latinLegalName || undefined,
    email: formValues.email,
    countryOfRegistration: formValues.countryOfRegistration,
    registrationType: formValues.registrationType,
    registrationNumber: formValues.registrationNumber,
    incorporationDate: formValues.incorporationDate,
    secondhandDealerNumber: formValues.secondhandDealerNumber || undefined,
    secondhandDealerNumberIssuingPrefecture:
      formValues.secondhandDealerNumberIssuingPrefecture || undefined,
    proofOfSecondHandDealerLicence:
      formValues.proofOfSecondHandDealerLicence.length > 0
        ? formValues.proofOfSecondHandDealerLicence.map(convertFileToBase64)
        : undefined,
    headquartersAddress: {
      addressLine1: formValues.headquartersStreet,
      addressLine2: formValues.headquartersStreet2 || undefined,
      city: formValues.headquartersCity,
      postalCode: formValues.headquartersPostalCode,
      country: formValues.headquartersCountry as string,
    },
    branchesAddresses: formValues.branchesAddresses.map((branch) => ({
      addressLine1: branch.street,
      addressLine2: branch.street2 || undefined,
      city: branch.city,
      postalCode: branch.postalCode,
      country: branch.country as string,
    })),
    registeredBusinessAddress: {
      addressLine1: formValues.registeredBusinessStreet,
      addressLine2: formValues.registeredBusinessStreet2 || undefined,
      city: formValues.registeredBusinessCity,
      postalCode: formValues.registeredBusinessPostalCode,
      country: formValues.registeredBusinessCountry as string,
    },
    articleOfIncorporation:
      formValues.articleOfIncorporation.map(convertFileToBase64),
    w9:
      formValues.w9.length > 0
        ? formValues.w9.map(convertFileToBase64)
        : undefined,
    investedCapital: {
      currency: formValues.investedCapitalCurrency,
      amount: formValues.investedCapitalAmount,
    },
  }
}
